import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import DefaultButton from '../../../components/Accessibility/DefaultButton';
import PageTitle from '../../../components/Accessibility/PageTitle';
import LoadingApp from '../../../components/LoadingApp';
import ModalAction from '../../../components/ModalAction';
import ModalFeedBack from '../../../components/ModalFeedBack';
import { BoletoLegado } from '../../../interfaces/entities/BoletoLegado';
import { getBoleto, getListaBoletos } from '../../../services/FinanceiroService';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import { base64toBlob } from '../../../utils/Base64ToBlob';
import { formatNumberToReais } from '../../../utils/MoneyFormat';
import './styles.css';
import { sxButtonGerarBoleto, sxSubtitleBold, sxTableContainer } from './sxProps';

function SegundaViaBoletoNovo() {
  const [boletos, setBoletos] = useState<BoletoLegado[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [loadingBoleto, setLoadingBoleto] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [selectedBoleto, setSelectedBoleto] = useState<BoletoLegado | null>(null);

  const accessibility = useAccessibility();
  const { textStyleAccessibility, contrastStyle, contrastBorderAccessibility } = useAccessibility();

  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontFamily: 'Unimed-Sans',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    ...textStyleAccessibility
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: '#F9F9F9'
    },
  }));

  useEffect(() => {
    loadBoletos();
  }, []);

  const loadBoletos = async () => {
    setLoadingData(true);
    try {
      const response = await getListaBoletos();
      setBoletos(response);
    } catch (error: any) {
      const errorMsg = error?.data?.message || error?.data?.mensagem || error?.mensagem || 'Estamos carregando suas informações de boleto. Tente novamente em instantes.';
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      setErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleClickGerarBoleto = (boleto: BoletoLegado) => {
    setSelectedBoleto(boleto);
    setConfirmModal(true);
  };

  const handleConfirmGerarBoleto = async () => {
    if (!selectedBoleto) return;

    setConfirmModal(false);
    setLoadingBoleto(true);

    try {
      const response = await getBoleto(
        selectedBoleto.prefixo,
        selectedBoleto.numero,
        selectedBoleto.parcela
      );

      if (response.base64) {
        response.base64 = response.base64.split(',')[1];
        abrirPdf(response.base64);
      } else {
        throw new Error('Boleto sem PDF disponível');
      }
    } catch (error: any) {
      const errorMsg = error?.data?.message || error?.data?.mensagem || error?.mensagem || 'Não foi possível gerar o boleto. Tente novamente.';
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      setErrorModal(true);
    } finally {
      setLoadingBoleto(false);
      setSelectedBoleto(null);
    }
  };

  const handleCancelModal = () => {
    setConfirmModal(false);
    setSelectedBoleto(null);
  };


  const abrirPdf = (base64: string) => {
    const blob = base64toBlob(base64);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl);
  };

  const handleCloseErrorModal = () => {
    setErrorModal(false);
  };

  const getRowAriaLabel = (boleto: BoletoLegado) => {
    return `Boleto com vencimento em ${boleto.vencimento}, valor ${formatNumberToReais(boleto.valor)}`;
  };

  const handleTableWrapperFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    if (e.target === tableWrapperRef.current && boletos.length > 0) {
      setTimeout(() => {
        buttonRefs.current[0]?.focus();
      }, 0);
    }
  };

  if (loadingData) {
    return <LoadingApp showLoading={true} />;
  }

  return (
    <div className="container-segunda-via">
      <LoadingApp showLoading={loadingBoleto} />
      <PageTitle marginBottom="10px" text="2ª via de boleto" />

      <Typography style={accessibility.textStyleAccessibility} sx={{ fontSize: '16px', color: '#4C4C4C' }}>
        Apenas serão exibidos boletos:
      </Typography>

      <ul style={{ marginBottom: '30px', color: '#4C4C4C' }}>
        <li>para a pessoa pagadora do contrato;</li>
        <li>se o boleto ainda não foi pago;</li>
      </ul>

      <Typography style={accessibility.textStyleAccessibility} sx={{ ...sxSubtitleBold, marginTop: '25px', marginBottom: '25px' }}>
        O boleto será aberto em uma nova janela e você precisará informar seu CPF para desbloquear o boleto. <br />
        Caso tenha dificuldades, desabilite o bloqueador de pop-ups do seu navegador e tente novamente.
      </Typography>

      <div className="atencao-container" style={{ ...accessibility.contrastStyle, ...accessibility.contrastBorderAccessibility }}>
        <FontAwesomeIcon icon={faTriangleExclamation} size="lg" />
        <div className="atencao-content">
          <Typography className="atencao-title" style={textStyleAccessibility}>
            ATENÇÃO:
          </Typography>
          <Typography className="atencao-text" style={textStyleAccessibility}>
            Atraso superior a 50 dias consecutivos pode resultar em <span className="atencao-text-bold">suspensão dos atendimentos</span>.
          </Typography>
          <Typography className="atencao-text" style={textStyleAccessibility}>
            Para evitar suspensão ou cancelamento do plano, o pagamento deve sempre iniciar pela mensalidade
            mais antiga em aberto.
          </Typography>
        </div>
      </div>

      {boletos.length === 0 ? (
        <Typography style={accessibility.textStyleAccessibility} sx={{ fontWeight: 'bold', marginTop: '20px' }}>
          Boleto(s) indisponível(is) para emissão. Em caso de dúvidas, entre em contato: (19) 3731-7900.
        </Typography>
      ) : (
        <>

          <Typography className="selecione-mensalidades-text" style={textStyleAccessibility}>
            Selecione abaixo as mensalidades que deseja quitar:
          </Typography>

          <div
            ref={tableWrapperRef}
            tabIndex={0}
            onFocus={handleTableWrapperFocus}
            style={{ outline: 'none' }}
          >
            <TableContainer
              className='container-table'
              component={Paper}
              sx={sxTableContainer}
              style={{ ...contrastBorderAccessibility, ...contrastStyle }}
            >
              <Table stickyHeader size="small" aria-label="tabela de boletos">
                <TableHead>
                  <TableRow>
                    <StyledTableCell style={textStyleAccessibility}><b>Vencimento</b></StyledTableCell>
                    <StyledTableCell align="center"><b>Valor</b></StyledTableCell>
                    <StyledTableCell align="center"><b></b></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boletos.map((boleto, index) => (
                    <StyledTableRow
                      key={`${index}-${boleto.numero}`}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <StyledTableCell component="th" scope="row">
                        {boleto.vencimento}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {formatNumberToReais(boleto.valor)}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <DefaultButton
                          ref={(el) => (buttonRefs.current[index] = el)}
                          variant="outlined"
                          sx={{ ...sxButtonGerarBoleto, minWidth: '170px' }}
                          style={accessibility.buttonStyleAccessibility}
                          onClick={() => handleClickGerarBoleto(boleto)}
                          onKeyDown={(e) => {
                            if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              if (index < boletos.length - 1) {
                                buttonRefs.current[index + 1]?.focus();
                              }
                            } else if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              if (index > 0) {
                                buttonRefs.current[index - 1]?.focus();
                              }
                            }
                          }}
                          aria-label={`${getRowAriaLabel(boleto)}, pressione Enter para gerar boleto`}
                        >
                          <b style={accessibility.buttonTextStyleAccessibility} aria-hidden="true">GERAR BOLETO</b>
                        </DefaultButton>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </>
      )}

      <ModalAction
        showModal={confirmModal}
        handleShowModal={handleCancelModal}
        icon="warning"
        maxWidth="465px"
        title="Atenção!"
        colorContainedButton='success'
        subTitle={
          `Para sua segurança, o arquivo do boleto está protegido. Para abrí-lo, será necessário digitar os <b>08 primeiros dígitos do CPF</b> do titular do plano. Ele abrirá numa nova aba, então <b>verifique se o bloqueador está desativado</b>.`
        }
        textOutlinedButton="CANCELAR"
        textContainedButton="GERAR BOLETO"
        handleOutlinedButton={handleCancelModal}
        handleContainedButton={handleConfirmGerarBoleto}
      />

      <ModalFeedBack
        showModal={errorModal}
        handleShowModal={handleCloseErrorModal}
        icon={'warning'}
        textButton={'Ok, entendi'}
        title={'Atenção'}
        showButtonModal
        handleButtonModal={handleCloseErrorModal}
        subTitle={errorMessage}
      />
    </div>
  );
}

export default SegundaViaBoletoNovo;
